import { useState, useEffect } from 'react';
import { BehaviorSubject } from 'rxjs';
import { fetchPortalUsers } from '../Api/Admin/PortalUsers/fetchPortalUsers';
import { PortalUserData, UserRoles } from '../types';
import { deletePortalUserApi } from '../Api/Admin/PortalUsers/deletePortalUserApi';
import { putPortalUser } from '../Api/Admin/PortalUsers/putPortalUser';
import { useSnackbar } from '../Context/SnackbarContext';
import { addError } from './Error';
import { PostPortalUserRequest, postPortalUser } from '../Api/Admin/PortalUsers/postPortalUser';
import { postClientMail } from '../Api/Admin/PortalUsers/postClientMail';
import { FormStateStatus, getFormCreateStateSubject, getFormsSubject } from './BulkForm';
import { FormSubmitState, getFormSubject, getFormSubmitStateSubject } from './AddPortalUsersToTeamFormState';
import { Role, useRoles } from './Role';
import { AddUserToTeamPayload, addUserToTeamApi } from '../Api/Admin/PortalUsers/portalUsersApi';
import { AccountSfIdMiss, getAccountName, getAccountSfId } from './Accounts';
import { getDefaultPreferences, GridPreferences, UserPreferences } from './User';
import { getAcctCustByAccountId, isCustom } from './AccountCustomization';
import { createSettingsApi } from '../Api/User/CreateUserSettingsApi';
import { extractApiErrorMessage } from '../utils/extractApiErrorMessage';
import { CallResult } from '../Api/callExternalApi';

export interface UserFormProps extends PostPortalUserRequest {
  dirty: boolean;
  valid: boolean;
  userCustomizations: UserPreferences;
  id: string;
  ui_element: React.ReactElement;
  account_gri: string;
  teamMissing?: boolean;
}

export interface UsePortalUsersReturn {
  setPortalUsersData: (portalUsers: PortalUserData[]) => void;
  clear: () => void;
  portalUsers: PortalUserData[];
  getPortalUsers: () => Promise<void>;
  createUsers: () => Promise<PromiseSettledResult<void>[]>;
  updateUser: () => Promise<void>;
  deletePortalUser: (portalUserId: string) => Promise<boolean | undefined>;
  createRequest: (requestBody: PostPortalUserRequest, userCustomizations: UserPreferences) => Promise<void>;
  addUserToTeam: (requestBody: AddUserToTeamPayload | PortalUserData, asManager: boolean) => Promise<void>;
  addUsersToTeam: () => Promise<void | undefined>;
}

export const defaultPortalUserData: PortalUserData[] = [];

const emitErrorState = (message?: string): void => addError('portal-user', message);

const ACCOUNT_LOOKUP_REFUSALS: Record<AccountSfIdMiss, string> = {
  lookup_failed: "Couldn't look up this user's account, so nothing was saved. Try again.",
  no_sf_record:
    "This user's account has no Salesforce record, so nothing was saved. Trying again won't help until it has one."
};

// A save that drops the account makes upstack_api clear the user's account, so a lookup that misses stops the save.
export const withAccountSfId = async (user: PortalUserData): Promise<CallResult<PortalUserData>> => {
  const { sfId, miss } = await getAccountSfId(user.account_id);
  if (miss) return { data: null, error: { message: ACCOUNT_LOOKUP_REFUSALS[miss] } };
  return { data: { ...user, account_id: sfId } as PortalUserData, error: null };
};

const portalUserSubject = new BehaviorSubject<PortalUserData[]>(defaultPortalUserData);
const portalUserUpdateSubject = new BehaviorSubject<PortalUserData | undefined>(undefined);
const portalUsersLoadingSubject = new BehaviorSubject<boolean>(false);

export const mergeAccountCustomizationIntoUserPreferences = (
  preferences: UserPreferences | undefined,
  accountId: string | undefined
): UserPreferences => {
  const mergedPreferences = structuredClone(preferences || getDefaultPreferences());
  const accountCustomization = getAcctCustByAccountId(accountId);

  if (!accountCustomization?.content) return mergedPreferences;

  const { inventory_grid, project_grid, project_details_grid, widgets, comments, order_request } =
    accountCustomization.content;

  if (inventory_grid) {
    mergedPreferences.content.inventory_grid_All = inventory_grid;
  }

  if (project_grid) {
    // A tab holding the account's layout, stamps or switch list reads them as the user's own and hides every later change.
    const muiConfig = { ...project_grid.muiConfig };
    delete muiConfig.columns;
    const tab: GridPreferences = { ...project_grid, muiConfig };
    delete tab.visibilityChangedAt;
    delete tab.visibilitySwitched;
    mergedPreferences.content.project_grid_Active = tab;
    mergedPreferences.content.project_grid_Completed = { ...tab };
  }

  if (project_details_grid) {
    mergedPreferences.content.project_details_grid = project_details_grid;
  }

  if (widgets && isCustom(widgets)) {
    mergedPreferences.content.widgets = widgets;
  }

  if (typeof comments === 'boolean') {
    mergedPreferences.content.comments = comments;
  }

  if (order_request) {
    mergedPreferences.content.order_request = order_request;
  }

  return mergedPreferences;
};

// Getters
export const getPortalUserSubject = (): BehaviorSubject<PortalUserData[]> => portalUserSubject;
export const getPortalUserUpdateSubject = (): BehaviorSubject<PortalUserData | undefined> => portalUserUpdateSubject;
export const getPortalUsers = (): PortalUserData[] => portalUserSubject.value;
export const getPortalUsersLoadingSubject = (): BehaviorSubject<boolean> => portalUsersLoadingSubject;
export const getUsersByAccount = (id: string) => {
  return portalUserSubject.getValue().filter((u) => u.account_id === id);
};

// Actions
export const updatePortalUser = (userData: PortalUserData) => portalUserUpdateSubject.next(userData);
export const deletePortalUser = async (portalUserId: string): Promise<boolean | undefined> => {
  const { error } = await deletePortalUserApi(portalUserId);

  if (error) return false;

  const updatedPortalUsers = getPortalUsers().filter((portalUser) => portalUser.id !== portalUserId);
  portalUserSubject.next(updatedPortalUsers);
  return true;
};

// Module-scoped setPortalUsersData for use in all functions
const setPortalUsersData = (portalUsers: PortalUserData[]) => portalUserSubject.next(portalUsers);

// Helper: retry once on failure
const getPortalUsersWithRetry = async () => {
  let firstError: unknown = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await getPortalUsersInternal();
      return;
    } catch (err) {
      firstError = err;
    }
  }
  throw firstError;
};

// Original getPortalUsers logic moved here for retry
const getPortalUsersInternal = async () => {
  if (portalUsersLoadingSubject.getValue()) return;

  portalUsersLoadingSubject.next(true);
  const { data, error } = await fetchPortalUsers();

  if (data) {
    emitErrorState();
    const formattedUsers = data.map((user: PortalUserData) => ({
      ...user,
      rolesDisplay: user?.roles?.length > 0 ? user.roles.map((role: UserRoles) => role?.name).join(', ') : ''
    }));
    setPortalUsersData(formattedUsers);
  }

  if (error) {
    emitErrorState(error.message);
    throw new Error(error.message);
  }

  portalUsersLoadingSubject.next(false);
};

export const usePortalUsers = (): UsePortalUsersReturn => {
  const [portalUsers, setPortalUsers] = useState(defaultPortalUserData);
  const [userDataUpdate, setUserDataUpdate] = useState<PortalUserData>();
  const { roles } = useRoles();
  const { setSnack } = useSnackbar();

  useEffect(() => {
    const userSubscription = portalUserSubject.subscribe((portalUsers) => setPortalUsers(portalUsers));
    const updateSubscription = getPortalUserUpdateSubject().subscribe((userData) => setUserDataUpdate(userData));

    return () => {
      if (userSubscription) userSubscription.unsubscribe();
      if (updateSubscription) updateSubscription.unsubscribe();
    };
  }, []);

  const getPortalUsers = getPortalUsersWithRetry;

  const updateUser = async () => {
    if (!userDataUpdate) return;
    const saving = await withAccountSfId(userDataUpdate);
    if (saving.error) {
      setSnack({ type: 'error', message: saving.error.message, open: true });
      portalUsersLoadingSubject.next(false);
      return;
    }

    const { error, data } = await putPortalUser(saving.data);

    if (data) {
      // Add account name if missing.
      if (data.account_id && !data.account_name) data.account_name = await getAccountName(data.account_id);
      const updatedAccountsPromises = data.accounts.map(async (acc) => {
        acc.name = acc.name || (await getAccountName(acc.id)) || '';
        return acc;
      });

      data.accounts = await Promise.all(updatedAccountsPromises);

      const updatedUsers = portalUsers.map((portalUser) =>
        portalUser.id === data.id
          ? { ...data, rolesDisplay: data.roles?.map((role) => role?.name).join(', ') || '' }
          : portalUser
      );

      portalUserSubject.next(updatedUsers);
      portalUserUpdateSubject.next(data);
      emitErrorState();
      setSnack({ message: 'User data saved successfully.', type: 'success', open: true });
    }

    if (error) {
      emitErrorState(error.message);
      setSnack({
        type: 'error',
        message: extractApiErrorMessage(error, 'Failed to update user.'),
        open: true
      });
    }

    portalUsersLoadingSubject.next(false);
  };

  const createRequest = async (requestBody: PostPortalUserRequest, userCustomizations: UserPreferences) => {
    const { data, error } = await postPortalUser(requestBody);
    const currentState = [...getFormCreateStateSubject().getValue()];

    if (error) {
      const update = {
        status: FormStateStatus.FAILED,
        data: { userCustomizations } as Partial<PortalUserData>,
        error: extractApiErrorMessage(error, error.message),
        requestBody
      };
      const errorIndex = currentState.findIndex(
        (i) => (i.requestBody as PostPortalUserRequest).email === requestBody.email
      );

      if (errorIndex > -1) {
        currentState[errorIndex] = update;
        getFormCreateStateSubject().next([...currentState]);
      } else {
        getFormCreateStateSubject().next([...currentState, update]);
      }
    }

    if (data) {
      getFormCreateStateSubject().next([
        ...currentState,
        { status: FormStateStatus.SUCCESS, data, error: undefined, requestBody }
      ]);

      const mergedUserCustomizations = mergeAccountCustomizationIntoUserPreferences(
        userCustomizations,
        data.account_id
      );

      await createSettingsApi({ ...mergedUserCustomizations, portal_user_id: Number(data.id) });
      await postClientMail({ userId: data.id });
    }
  };

  const createUsers = async () => {
    const forms = getFormsSubject().getValue();
    const promises: Promise<void>[] = forms.map((form) => {
      const {
        first_name,
        last_name,
        email,
        roles_attributes,
        account_id,
        team_id,
        userCustomizations,
        accounts,
        otp_required_for_login
      } = form as UserFormProps;

      const requestBody = {
        first_name,
        last_name,
        email,
        roles_attributes,
        account_id,
        team_id,
        accounts,
        otp_required_for_login
      };
      return createRequest(requestBody, userCustomizations);
    });

    return await Promise.allSettled(promises);
  };

  const addUserToTeam = async (requestBody: AddUserToTeamPayload | PortalUserData, asManager: boolean) => {
    if (asManager) return addManagerToTeam(requestBody as PortalUserData);
    return addMemberToTeam(requestBody as AddUserToTeamPayload);
  };

  const addMemberToTeam = async (requestBody: AddUserToTeamPayload) => {
    const { error, data } = await addUserToTeamApi(requestBody as AddUserToTeamPayload);
    handleUserAddedToTeam(data, error, requestBody);
  };

  const addManagerToTeam = async (requestBody: PortalUserData) => {
    const saving = await withAccountSfId(requestBody);
    if (saving.error) return handleUserAddedToTeam(null, saving.error, requestBody, true);
    const { data, error } = await putPortalUser(saving.data);
    handleUserAddedToTeam(data, error, requestBody, true);
  };

  const handleUserAddedToTeam = (
    data: PortalUserData | null,
    error: { message: string } | null,
    requestBody: PortalUserData | AddUserToTeamPayload,
    manager: boolean = false
  ) => {
    const currentState = getFormSubmitStateSubject().getValue();

    const stateIndex = currentState.findIndex((i) => {
      if (manager) {
        return (i.requestBody as PortalUserData).id === (requestBody as PortalUserData).id;
      } else {
        return (i.requestBody as AddUserToTeamPayload).userId === (requestBody as AddUserToTeamPayload).userId;
      }
    });

    let update: FormSubmitState = currentState[stateIndex];

    if (error) {
      update = {
        status: FormStateStatus.FAILED,
        data: currentState[stateIndex].data,
        error: error.message,
        requestBody
      };
    }

    if (data) {
      update = { status: FormStateStatus.SUCCESS, data, error: undefined, requestBody };
      const updatedUsers = portalUsers.map((portalUser: PortalUserData) =>
        portalUser.id === data.id ? data : portalUser
      );

      setPortalUsersData(updatedUsers);
    }

    if (stateIndex > -1) {
      currentState[stateIndex] = update;
      getFormSubmitStateSubject().next([...currentState]);
    } else {
      getFormSubmitStateSubject().next([...currentState, update]);
    }
  };

  const addUsersToTeam = async () => {
    const form = getFormSubject().getValue();

    if (form.portal_users.length === 0) return;
    const teamManagerRole = roles.find((r) => r.name === Role.TEAM_MANAGER);

    const formSubmitState = form.portal_users.map((user: PortalUserData): FormSubmitState => {
      let userUpdate: AddUserToTeamPayload | PortalUserData;

      if (form.type === 'manager') {
        userUpdate = {
          ...user,
          team_id: Number(form.team_id),
          roles: [...user.roles, teamManagerRole as UserRoles]
        };
      } else {
        userUpdate = {
          userId: user.id,
          teamId: Number(form.team_id)
        };
      }

      const formState = {
        status: FormStateStatus.SUBMITTING,
        data: user,
        requestBody: userUpdate
      };

      return formState;
    });

    getFormSubmitStateSubject().next(formSubmitState);

    const promises = formSubmitState.map(async (formState) => {
      return addUserToTeam(formState.requestBody, form.type === 'manager');
    });

    await Promise.all(promises);
  };

  const clear = () => portalUserSubject.next(defaultPortalUserData);

  return {
    setPortalUsersData,
    clear,
    portalUsers,
    getPortalUsers,
    deletePortalUser,
    updateUser,
    createUsers,
    createRequest,
    addUserToTeam,
    addUsersToTeam
  };
};
